/**
 * Ask the server for the LiveKit room token, waiting in line when every AI
 * interviewer is busy and retrying through brief network trouble.
 *
 * Server answers (POST /api/ai-interviews/session/livekit-token):
 * - 200                         → admitted; token + url.
 * - 503 INTERVIEWER_BUSY        → in the lobby; position, estimate, retryAfterMs.
 * - 503 INTERVIEWER_CONNECT_FAILED, network error, 5xx, 429 → transient; retried.
 * - anything else               → a real refusal (completed, rejected, …); thrown.
 *
 * Never shows a "slots in use" error: waiting is reported through onWaiting,
 * retrying through onConnecting.
 */

const MAX_LOBBY_WAIT_MS = 45 * 60 * 1000;
const DEFAULT_LOBBY_RETRY_MS = 10000;
const TRANSIENT_RETRY_DELAYS_MS = [2000, 4000, 8000, 10000];
const REQUEST_TIMEOUT_MS = 45000;

export const CONNECT_FAILED_MESSAGE =
  "We couldn't connect you to your interviewer. Please check your internet connection and try again.";
export const LOBBY_TIMEOUT_MESSAGE =
  'Our interviewers are still busy. Please try again in a few minutes.';

/** Thrown when the page went away mid-wait; callers should just stop. */
export class AdmissionCancelledError extends Error {
  constructor() {
    super('Admission cancelled');
    this.name = 'AdmissionCancelledError';
  }
}

/** The candidate could not be connected; the lobby shows its "try again" view. */
export class AdmissionFailedError extends Error {
  constructor(message, code) {
    super(message);
    this.name = 'AdmissionFailedError';
    this.code = code;
  }
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function isBusy(code) {
  // CAPACITY_REACHED: what servers before the lobby sent for any dispatch error.
  return code === 'INTERVIEWER_BUSY' || code === 'CAPACITY_REACHED';
}

function isTransient(status, code) {
  return code === 'INTERVIEWER_CONNECT_FAILED' || status === 429 || status >= 500;
}

/**
 * @param {object} opts
 * @param {string} opts.url
 * @param {Record<string,string>} opts.headers mutated when the server issues a session token
 * @param {object} opts.body mutated when the server issues a session id
 * @param {(lobby: {position:number, estimatedWaitMinutes:{low:number,high:number}} | null) => void} [opts.onWaiting]
 * @param {(attempt: number) => void} [opts.onConnecting]
 * @param {() => boolean} [opts.isCancelled]
 * @returns {Promise<object>} the token response body
 */
export async function requestInterviewToken({
  url,
  headers,
  body,
  onWaiting = () => {},
  onConnecting = () => {},
  isCancelled = () => false,
}) {
  const startedAt = Date.now();
  let transientFailures = 0;

  const checkCancelled = () => {
    if (isCancelled()) throw new AdmissionCancelledError();
  };

  for (;;) {
    checkCancelled();

    let res;
    let data = {};
    try {
      res = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
      });
      data = await res.json().catch(() => ({}));
    } catch (err) {
      res = null; // network failure or timeout — treated as transient below
    }
    checkCancelled();

    if (res && res.ok) {
      onWaiting(null);
      return data;
    }

    // Keep the session the server created, so the next try keeps our place.
    if (data.sessionId) body.sessionId = data.sessionId;
    if (data.sessionToken) headers['x-session-token'] = data.sessionToken;

    const status = res ? res.status : 0;
    const code = data.code || '';

    if (res && isBusy(code)) {
      transientFailures = 0;
      if (Date.now() - startedAt > MAX_LOBBY_WAIT_MS) {
        throw new AdmissionFailedError(LOBBY_TIMEOUT_MESSAGE, 'LOBBY_TIMEOUT');
      }
      onWaiting({
        position: data.lobby?.position || null,
        estimatedWaitMinutes: data.lobby?.estimatedWaitMinutes || null,
      });
      await sleep(data.retryAfterMs || DEFAULT_LOBBY_RETRY_MS);
      continue;
    }

    if (!res || isTransient(status, code)) {
      const delay = TRANSIENT_RETRY_DELAYS_MS[transientFailures];
      transientFailures += 1;
      if (delay === undefined) {
        throw new AdmissionFailedError(CONNECT_FAILED_MESSAGE, code || 'NETWORK');
      }
      onConnecting(transientFailures);
      await sleep(Math.max(delay, data.retryAfterMs || 0));
      continue;
    }

    const err = new Error(data.error || data.message || 'Failed to get LiveKit token');
    err.code = code;
    throw err;
  }
}
